import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coins, Sparkles, ArrowDownLeft, ArrowUpRight, Info, Zap } from 'lucide-react';
import DesktopHeader from '../components/DesktopHeader';
import BottomNav from '../components/BottomNav';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const Rewards = () => {
  const nav = useNavigate();
  const [points, setPoints] = useState(0);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/rewards`, authCfg());
      setPoints(data.points);
      setTxns(data.transactions || []);
    } catch (e) {
      if (e?.response?.status === 401) nav('/login');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 md:pb-8">
      <DesktopHeader />

      <div className="max-w-[900px] mx-auto px-5 md:px-6 pt-4 md:pt-8">
        <button onClick={() => nav(-1)} className="md:hidden mb-3 inline-flex items-center gap-1 text-sm text-neutral-600"><ArrowLeft className="w-4 h-4" /> Back</button>

        {/* Points hero */}
        <div data-testid="rewards-hero" className="bg-white border border-neutral-200 rounded-2xl p-5 md:p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
              <Coins className="w-6 h-6 text-[#D97706]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Clean24 Rewards</p>
              <p className="mt-1 text-3xl md:text-4xl font-extrabold text-neutral-900" data-testid="rewards-points-value">
                {points.toLocaleString('en-IN')} <span className="text-lg font-bold text-neutral-500">pts</span>
              </p>
              <p className="mt-0.5 text-xs text-neutral-500">1 point = ₹1 · redeem at checkout</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => nav('/services/home-cleaning')} className="inline-flex items-center gap-1.5 bg-[#D97706] hover:bg-[#B45309] text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors">
              <Sparkles className="w-4 h-4" /> Book & earn more
            </button>
            <button onClick={() => nav('/wallet')} className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 hover:border-[#2E7D32] font-semibold text-sm px-4 py-2 rounded-lg transition-colors text-neutral-700">
              Wallet
            </button>
          </div>
        </div>

        {/* Earning rules */}
        <div className="mt-4 bg-white border border-neutral-200 rounded-2xl p-4" data-testid="rewards-rules">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-[#D97706]" />
            <p className="font-bold text-sm">How to earn</p>
          </div>
          <ul className="text-xs text-neutral-600 space-y-1.5">
            <li className="flex items-center justify-between border-b border-dashed border-neutral-100 pb-1.5"><span>Any one-off booking</span><span className="font-bold text-[#D97706]">5% of order value</span></li>
            <li className="flex items-center justify-between border-b border-dashed border-neutral-100 pb-1.5"><span>Weekly subscription</span><span className="font-bold text-[#D97706]">+20 pts</span></li>
            <li className="flex items-center justify-between border-b border-dashed border-neutral-100 pb-1.5"><span>Monthly subscription</span><span className="font-bold text-[#D97706]">+50 pts</span></li>
            <li className="flex items-center justify-between"><span>Gold membership</span><span className="font-bold text-[#D97706]">+100 pts</span></li>
          </ul>
        </div>

        {/* Transactions */}
        <div className="mt-4 bg-white border border-neutral-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
            <p className="font-bold text-sm">Reward history</p>
            <span className="text-xs text-neutral-500">{txns.length} entries</span>
          </div>
          {loading ? (
            <p className="p-6 text-center text-sm text-neutral-500">Loading…</p>
          ) : txns.length === 0 ? (
            <div className="p-8 text-center">
              <Coins className="w-8 h-8 text-neutral-300 mx-auto" />
              <p className="mt-2 text-sm text-neutral-500">Book a service to start earning points</p>
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100" data-testid="rewards-txn-list">
              {txns.map((t) => (
                <li key={t.id} className="px-4 py-3 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${t.type === 'earn' ? 'bg-[#FEF3C7]' : 'bg-[#E5E7EB]'}`}>
                    {t.type === 'earn' ? <ArrowDownLeft className="w-4 h-4 text-[#D97706]" /> : <ArrowUpRight className="w-4 h-4 text-neutral-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{t.reason}</p>
                    <p className="text-[11px] text-neutral-500 mt-0.5">{new Date(t.created_at).toLocaleString('en-IN')} {t.order_id ? `· ${t.order_id}` : ''}</p>
                  </div>
                  <span className={`font-bold text-sm ${t.type === 'earn' ? 'text-[#D97706]' : 'text-neutral-500'}`}>
                    {t.type === 'earn' ? '+' : '−'} {t.points} pts
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-3 text-[11px] text-neutral-500 flex items-center gap-1"><Info className="w-3 h-3" /> Points are auto-credited when your booking is confirmed. Redeem up to 20% of any order at checkout.</p>
      </div>

      <BottomNav />
    </div>
  );
};

export default Rewards;
